import { useMemo } from 'react';

import { ExpandedState } from '@tanstack/react-table';

import { SectionPaginationApi } from '@/components/data-table/nested-pagination';

import { APPROVALS_PROJECTS_SECTION_ID } from '../constants/approvals-sections';
import { createEmptyEntriesRow, createLoadMoreRow } from '../helpers/approval-load-more-row.helper';
import {
  ApprovalLoadMoreRow,
  ApprovalMemberRow,
  ApprovalProjectRow,
} from '../types/time-tracking-approvals';

// =============================================================================
// Sentinel rows are synthetic: a "loading more" spinner for a section fetching its next page,
// and a "no entries" placeholder for an expanded member that loaded nothing. They are appended
// to lists typed for real rows, because TanStack types the whole tree with ONE row type. These
// three one-liners are the only place that cast — nothing below deals with types again.
// =============================================================================

const asMemberRows = (
  rows: (ApprovalMemberRow | ApprovalLoadMoreRow)[]
): ApprovalProjectRow['members'] => rows as ApprovalProjectRow['members'];

const asEntryRows = (
  rows: (ApprovalMemberRow['timeEntries'][number] | ApprovalLoadMoreRow)[]
): ApprovalMemberRow['timeEntries'] => rows as ApprovalMemberRow['timeEntries'];

const asProjectRow = (row: ApprovalLoadMoreRow): ApprovalProjectRow =>
  row as unknown as ApprovalProjectRow;

// =============================================================================
// Which rows need a sentinel
// =============================================================================

// A project/member row's `id` IS its pagination section id (`project-…`, `project-…-member-…`),
// which is what lets the code below look sections up straight from the row — same as
// `use-lazy-approval-data`, which registers the loaders under those ids.

/** Row ids that need a sentinel row, resolved once per render from live pagination state. */
interface SentinelTargets {
  /** Sections whose NEXT page is in flight → append a "loading more" row. */
  loadMore: Set<string>;
  /** Expanded members that finished loading with zero entries → show "no entries". */
  emptyMembers: Set<string>;
}

interface FindSentinelTargetsParams {
  projects: ApprovalProjectRow[];
  expanded: ExpandedState;
  sectionPagination: SectionPaginationApi;
  loadingRowIds: Set<string>;
  isFetchingMoreProjects: boolean;
}

/** True when a member's worklog section is loaded, complete, and turned out to be empty. */
const isEmptySection = ({
  member,
  expanded,
  sectionPagination,
  loadingRowIds,
}: {
  member: ApprovalMemberRow;
  expanded: Record<string, boolean>;
  sectionPagination: SectionPaginationApi;
  loadingRowIds: Set<string>;
}): boolean => {
  const section = sectionPagination.getSection(member.id);

  return (
    expanded[member.id] &&
    section !== undefined &&
    !section.hasNextPage &&
    !loadingRowIds.has(member.id) &&
    member.timeEntries.length === 0
  );
};

const findSentinelTargets = ({
  projects,
  expanded,
  sectionPagination,
  loadingRowIds,
  isFetchingMoreProjects,
}: FindSentinelTargetsParams): SentinelTargets => {
  const loadMore = new Set<string>();
  const emptyMembers = new Set<string>();
  const expandedMap = (typeof expanded === 'object' ? expanded : {}) as Record<string, boolean>;

  if (isFetchingMoreProjects) loadMore.add(APPROVALS_PROJECTS_SECTION_ID);

  for (const project of projects) {
    if (sectionPagination.getSection(project.id)?.isFetchingNextPage) loadMore.add(project.id);

    for (const member of project.members) {
      if (sectionPagination.getSection(member.id)?.isFetchingNextPage) loadMore.add(member.id);
      if (isEmptySection({ member, expanded: expandedMap, sectionPagination, loadingRowIds })) {
        emptyMembers.add(member.id);
      }
    }
  }

  return { loadMore, emptyMembers };
};

// =============================================================================
// Building the tree
// =============================================================================

/** Copy of a member with a spinner row appended after its loaded entries. */
const withLoadMoreEntry = (member: ApprovalMemberRow): ApprovalMemberRow => ({
  ...member,
  timeEntries: asEntryRows([...member.timeEntries, createLoadMoreRow(member.id)]),
});

/** Copy of a member whose only child is the "no entries" placeholder. */
const withEmptyEntriesRow = (member: ApprovalMemberRow): ApprovalMemberRow => ({
  ...member,
  timeEntries: asEntryRows([createEmptyEntriesRow(member.id)]),
});

/**
 * Members of a project with their sentinels applied. Returns the ORIGINAL array when no member
 * changed, so untouched projects can keep their identity (see `buildTree`).
 */
const applyMemberSentinels = (
  project: ApprovalProjectRow,
  targets: SentinelTargets
): ApprovalProjectRow['members'] => {
  const members = project.members.map(member => {
    if (targets.loadMore.has(member.id)) return withLoadMoreEntry(member);
    if (targets.emptyMembers.has(member.id)) return withEmptyEntriesRow(member);
    return member;
  });

  const changed = members.some((member, index) => member !== project.members[index]);
  return changed ? members : project.members;
};

/**
 * `projects` with every sentinel row in place, reusing the original objects wherever nothing
 * changed — TanStack rebuilds its row model whenever an object identity changes, and the
 * memoized cells re-render with it, so identity is a performance contract here.
 */
const buildTree = (
  projects: ApprovalProjectRow[],
  targets: SentinelTargets
): ApprovalProjectRow[] => {
  if (targets.loadMore.size === 0 && targets.emptyMembers.size === 0) return projects;

  const tree = projects.map(project => {
    const members = applyMemberSentinels(project, targets);
    const needsMemberLoader = targets.loadMore.has(project.id);

    if (members === project.members && !needsMemberLoader) return project;

    return {
      ...project,
      members: needsMemberLoader
        ? asMemberRows([...members, createLoadMoreRow(project.id)])
        : members,
    };
  });

  if (targets.loadMore.has(APPROVALS_PROJECTS_SECTION_ID)) {
    tree.push(asProjectRow(createLoadMoreRow(APPROVALS_PROJECTS_SECTION_ID)));
  }

  return tree;
};

// =============================================================================
// Hook
// =============================================================================

/** Stable key for a set of row ids, so the memo below can depend on primitives. */
const toKey = (rowIds: Set<string>): string => [...rowIds].sort().join(',');

const fromKey = (key: string): Set<string> => new Set(key ? key.split(',') : []);

interface UseApprovalSentinelRowsParams {
  /** The loaded tree WITHOUT sentinels (what the pagination walker keeps using). */
  enrichedData: ApprovalProjectRow[];
  expanded: ExpandedState;
  /** Inner-level (members/worklogs) pagination state. */
  sectionPagination: SectionPaginationApi;
  /** Sections still loading their FIRST page. */
  loadingRowIds: Set<string>;
  isFetchingMoreProjects: boolean;
}

/**
 * The tree the approvals table renders: `enrichedData` plus per-section "loading more" and
 * "no entries" sentinel rows, injected at the right nesting depth as real sub-rows.
 *
 * Targets are recomputed every render on purpose: a section's `isFetchingNextPage` lives in a ref
 * inside the pagination registry, which force-renders WITHOUT changing any object identity — a
 * memo keyed on identity alone would miss fetch-state flips. The tree itself is memoized on the
 * targets' KEYS (plain strings), which keeps every dependency of that memo declared.
 */
export const useApprovalSentinelRows = ({
  enrichedData,
  expanded,
  sectionPagination,
  loadingRowIds,
  isFetchingMoreProjects,
}: UseApprovalSentinelRowsParams): ApprovalProjectRow[] => {
  const targets = findSentinelTargets({
    projects: enrichedData,
    expanded,
    sectionPagination,
    loadingRowIds,
    isFetchingMoreProjects,
  });

  const loadMoreKey = toKey(targets.loadMore);
  const emptyMembersKey = toKey(targets.emptyMembers);

  return useMemo(
    () =>
      buildTree(enrichedData, {
        loadMore: fromKey(loadMoreKey),
        emptyMembers: fromKey(emptyMembersKey),
      }),
    [enrichedData, loadMoreKey, emptyMembersKey]
  );
};
